// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { PermissionService } from '../services/permission.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as permissionAction from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { PERMISSION_NGRX_CONSTANT } from '../../../../constants/ngrx-store/permission';

/**
 * This effect is for listen master permission actions, execute master permission service call and dispatch in master permission reducer.
 * For master permission api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 27/04/2020 (Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created master permission effect
 */
@Injectable()
export class PermissionEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private permissionService: PermissionService) { }

  /**
   *  Master Permission Effect
   * @param type :  action name.
   * @param payload : object to identify common master permission method base on module and method.
   * @returns Empty action dispatch to reducer.
   */
  masterPermissionCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<permissionAction.PermissionCommonAction>(permissionAction.ActionTypes.PERMISSION_COMMON),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common: any = PERMISSION_NGRX_CONSTANT[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (common.CACHING && !common.INDIVIDUAL && state.permission && state.permission[common.STATE]) {
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.permission[common.STATE] });
        }
        // make empty action call for skip
        return of(new globalActions.EmptyAction({}));
      } else if (common.CACHING && common.INDIVIDUAL && state.permission &&
        state.permission[common.STATE] &&
        state.permission[common.STATE][action.payload.data[common.UNIQUE_KEY]]) {

        // data send from redux store if already available
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.permission[common.STATE][action.payload.data[common.UNIQUE_KEY]] });
        }

        return of(new globalActions.EmptyAction({}));
      }

      // make api call
      return this.permissionService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            let actions: any[] = [];

            // this code added to else condition of success notification
            // if (action.payload.callback) {
            //   action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            // }
            // dispatch master permission success action
            actions = [new permissionAction.PermissionCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: permissionAction.ActionTypes.PERMISSION_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`,
              }));
            } else {
              // tslint:disable-next-line:no-unused-expression
              action.payload.callback && action.payload.callback({ success: true, data });
            }

            return actions;
          }),
          catchError(error => {
            console.error(error);
            error.error = error.error || {};
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: permissionAction.ActionTypes.PERMISSION_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          }));
    }),
  ));


}
